/**
 * Состояние картинки поста, выведенное из модели рендера, и гейт утверждения.
 *
 * Чистый модуль: ни React, ни DOM — как `preview/validation.ts`. Его читают три места
 * (полоса визуала, экран черновика, пакетный экран), и второго вывода состояния быть не
 * должно: сегодня «картинки нет» и «человек отклонил предложение» отличаются одним полем,
 * и посчитанные в трёх местах порознь они разойдутся первыми.
 *
 * ПОЧЕМУ ВЫВОД, А НЕ ПОЛЕ КОНТРАКТА. Контракт отдаёт `{verdict, reason, origin, proposal}` —
 * четыре независимых значения. Экран же обязан различать ПЯТЬ исходов и подписать каждый
 * своим набором кнопок; выводить их по месту, из трёх `if` подряд, значит гарантировать, что
 * однажды полоса скажет «принята», а пузырь нарисует пост без картинки. Поэтому вывод один
 * и в одном месте, а `attached` определяется НАЛИЧИЕМ БАЙТОВ В ВЕРСИИ, а не вердиктом:
 * подписывается версия, и правду о картинке говорит она.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Состояния «перерисовывается»: в модели рендера нет ни одного
 * поля, которое отличало бы переделку визуала от переделки текста, а `status ===
 * 'changes_requested'` отвечает на другой вопрос. Выдумывать его значило бы обещать
 * человеку знание, которого у экрана нет.
 */

import type { DraftContent, DraftState } from '@smm/contracts';
import {
  POSTER_REASON_PREFIX,
  type PosterHookFormat,
  type PosterMood,
  type PosterSpec,
  posterDateLabel,
} from '@smm/contracts/schemas';
import type {
  MediaOrigin,
  MediaRef,
  RenderModel,
  VisualLayout,
  VisualVerdict,
} from '../../api/types.ts';
import { VISUAL_HEIGHT, VISUAL_WIDTH } from '../../api/types.ts';
import type { UiIssue } from '../contracts.ts';

/** Русские имена макетов живут в UI: контракт даёт перечисление, фразу даёт экран. */
export const VISUAL_LAYOUT_LABEL: Readonly<Record<VisualLayout, string>> = {
  contrast: 'сравнение',
  sequence: 'порядок шагов',
  stat: 'цифра',
  // «Перечисление», а не «список»: на экране рядом стоит «порядок шагов», и два слова
  // обязаны читаться как разные вещи — список ничего не утверждает о порядке.
  list: 'перечисление',
  cover: 'обложка',
};

/**
 * Форматы крючка обложки по-русски (docs/phase-2/10-posters.md). Те же слова, что в задании
 * Ильи и в `knowledge/publishing/poster.md`: человек читает в полосе то же имя формата, каким
 * его знает дизайнер.
 */
export const POSTER_FORMAT_LABEL: Readonly<Record<PosterHookFormat, string>> = {
  question: 'риторический вопрос',
  open_question: 'вопрос с многоточием',
  phrase: 'фраза',
  headline: 'заголовок коротко',
  thesis: 'тезис',
  consequence: 'следствие для читателя',
  number: 'число',
  stakes: 'ставки',
  deadline: 'срок',
  deadpan: 'сухая констатация',
  reader: 'ситуация читателя',
  juxtaposition: 'сопоставление',
};

export const POSTER_MOOD_LABEL: Readonly<Record<PosterMood, string>> = {
  neutral: 'нейтральное',
  serious: 'серьёзное',
  alarming: 'тревожное',
  optimistic: 'позитивное',
  curious: 'любопытное',
  ironic: 'ироничное',
};

/**
 * Пять исходов плюс «вердикта нет». Размеченное объединение, а не мешок nullable-полей:
 * набор кнопок и текст последствия у каждого исхода свои, и `if (verdict === 'drawn' &&
 * proposal === null && media === null)` в разметке читался бы как случайность.
 */
export type VisualView =
  /** Версия создана до появления механизма (или фикстура): врать за неё нечем. */
  | { readonly state: 'unset' }
  /** Сервис предложил картинку, человек ещё не решил. Единственное состояние с гейтом. */
  | {
      readonly state: 'proposed';
      readonly layout: VisualLayout;
      readonly reason: string;
      readonly insteadOfText: string;
      readonly mediaId: string;
    }
  /** Байты стоят в версии и входят в `content_hash`. */
  | {
      readonly state: 'attached';
      /** `null` — вердикта не выносили, а картинка есть: так выглядит правка до 0015. */
      readonly verdict: VisualVerdict | null;
      readonly reason: string;
      readonly origin: MediaOrigin | null;
      /** Картинка версии — обложка (docs/phase-2/10-posters.md); `null` — другая картинка. */
      readonly poster: PosterSpec | null;
    }
  /**
   * Сервис решил не рисовать. Штатный исход, утверждению не мешает. `poster` — отказ вынесла
   * ОБЛОЖКА (приставка `POSTER_REASON_PREFIX`), а не вердикт инфографики: у бренда с
   * обложкой полоса предлагает нарисовать её, а не макет с данными.
   */
  | { readonly state: 'none'; readonly reason: string; readonly poster: boolean }
  /**
   * Сервис решил рисовать, а картинки в версии нет. Два пути сюда — человек отклонил
   * предложение и рендер не дошёл до версии, — и модель рендера их НЕ различает: у
   * отклонённого предложения `state` уже не `pending`, поэтому `read-model.ts::visualOf`
   * не отдаёт его вовсе. Склеены они не для краткости, а потому что различить их нечем;
   * фраза состояния называет оба, а не выбирает один наугад.
   *
   * `poster` — третий путь, и его различить есть чем: вердикт с приставкой обложки остаётся у
   * версии, из которой человек убрал обложку (`domain/human-edit.ts` вердикт не обнуляет).
   * Полоса тогда предлагает нарисовать обложку заново, а не одну инфографику.
   */
  | { readonly state: 'undrawn'; readonly reason: string; readonly poster: boolean };

/** Ровно то, что нужно для вывода: остальные поля модели рендера здесь не при чём. */
export interface VisualSubject {
  readonly visual: RenderModel['visual'];
  readonly media_ref: RenderModel['media_ref'];
  readonly status: DraftState;
}

/**
 * Порядок веток нормативен.
 *
 * `proposed` идёт ПЕРВЫМ: неразобранное предложение закрывает утверждение независимо от
 * того, стоит ли уже картинка в версии (человек мог принести свою, пока предложение висело).
 * `attached` идёт РАНЬШЕ вердикта `none`: человек вправе приложить картинку к посту, который
 * сервис рисовать не собирался, и вердикт при этом остаётся прежним — правду о содержании
 * говорят байты.
 */
export function visualViewOf(m: VisualSubject): VisualView {
  const v = m.visual;
  const hasMedia = m.media_ref !== null;
  if (v === null) {
    return hasMedia
      ? { state: 'attached', verdict: null, reason: '', origin: null, poster: null }
      : { state: 'unset' };
  }
  if (v.proposal !== null) {
    return {
      state: 'proposed',
      layout: v.proposal.layout,
      reason: v.reason,
      insteadOfText: v.proposal.instead_of_text,
      mediaId: v.proposal.media_id,
    };
  }
  if (hasMedia) {
    return {
      state: 'attached',
      verdict: v.verdict,
      reason: v.reason,
      origin: v.origin,
      poster: v.poster ?? null,
    };
  }
  const poster = v.reason.startsWith(POSTER_REASON_PREFIX);
  if (v.verdict === 'none') return { state: 'none', reason: v.reason, poster };
  return { state: 'undrawn', reason: v.reason, poster };
}

/**
 * Причина без приставки обложки — для показа. Приставка — метка для машины (по ней вьюха доли
 * картинок не считает обложки), а человеку рядом со словом «обложка» она повторяет его дважды.
 */
export function posterReasonText(reason: string): string {
  return reason.startsWith(POSTER_REASON_PREFIX)
    ? reason.slice(POSTER_REASON_PREFIX.length).trim()
    : reason;
}

/**
 * Состояния, в которых кнопка утверждения на экране есть, — то есть те, где гейт может
 * что-то выключить. Набор повторяет `APPROVABLE` из `services/backend/src/domain/approve.ts`
 * и обязан с ним совпадать: бэкенд отбивает одобрение с неразобранным предложением в
 * `checkApprovable` (`domain/approve.ts:121`), и выключенная кнопка — это то же правило,
 * показанное заранее, а не второе правило рядом.
 */
const GATED_STATUSES: ReadonlySet<DraftState> = new Set(['pending_review', 'approved', 'failed']);

/**
 * ГЕЙТ. Ровно одна ветка что-то возвращает.
 *
 * Гейт выражен блокирующей `UiIssue`, а не отдельным пропом, потому что единственный
 * механизм, выключающий кнопку утверждения на этом экране, — `hasBlocking(issues)`
 * (`DecisionPanel`, `ApproveDialog`, карточка пакетного экрана). Второй способ выключать
 * ту же кнопку означал бы два правила, которые однажды ответят по-разному.
 *
 * `state: 'none'` НЕ блокирует ничего, и это утверждение, а не умолчание: решение Ильи
 * 06.09.2026 — «картинки нет, значит утверждать можно сразу». Оно закрыто тестом, иначе
 * однажды станет «для порядка заблокируем и этот случай тоже».
 */
export function visualIssues(m: VisualSubject): readonly UiIssue[] {
  const view = visualViewOf(m);
  if (view.state !== 'proposed' || !GATED_STATUSES.has(m.status)) return [];
  return [
    {
      level: 'blocking',
      code: 'visual_undecided',
      message:
        'По картинке нет решения: сервис предложил визуал, и пока он не принят и не отклонён, ' +
        'пост не утверждается. Оба ответа равноценны — «Принять картинку» или «Отклонить» ' +
        'в полосе визуала под постом.',
    },
  ];
}

/** Вторая строка шапки полосы. Одна фраза на состояние, без склеек по условиям в разметке. */
export function visualHeadline(view: VisualView): string {
  switch (view.state) {
    case 'unset':
      return 'вердикта по этой версии нет';
    case 'proposed':
      return `предложение сервиса · макет «${VISUAL_LAYOUT_LABEL[view.layout]}»`;
    case 'attached':
      if (view.poster !== null) {
        return `обложка в версии · ${POSTER_FORMAT_LABEL[view.poster.hook_format]} · на ${posterDateLabel(view.poster.date)}`;
      }
      return view.origin === 'human'
        ? 'картинка в версии · загружена человеком'
        : view.origin === 'writer'
          ? 'картинка в версии · нарисована сервисом'
          : 'картинка в версии';
    case 'none':
      return view.poster ? 'обложки нет' : 'сервис решил не рисовать';
    case 'undrawn':
      return view.poster ? 'обложку убрали из версии' : 'картинки в версии нет';
  }
}

/**
 * Расхождение дня обложки с выбранным слотом — подсказка диалога одобрения ДО того, как бэкенд
 * отобьёт одобрение (`assertPosterDay`). Сравнение — по дню слота В ЗОНЕ БРЕНДА, а она в
 * `slot_local` уже посчитана бэкендом (`YYYY-MM-DDTHH:MM`): второй расчёт зоны в браузере
 * разошёлся бы с сервером на переходе часов.
 */
export function posterSlotMismatch(
  view: VisualView,
  slotLocal: string | null,
): { readonly posterDay: string; readonly slotDay: string } | null {
  if (view.state !== 'attached' || view.poster === null || slotLocal === null) return null;
  const slotDay = slotLocal.slice(0, 10);
  return slotDay === view.poster.date ? null : { posterDay: view.poster.date, slotDay };
}

/**
 * Дескриптор картинки-КАНДИДАТА для превью — и только для него.
 *
 * Три поля дескриптора (`mime_type`, `sha256`, `bytes`) модель рендера у предложения не
 * отдаёт, потому что дескриптор собирает бэкенд в момент принятия (`domain/visual.ts`,
 * `prepare`). Здесь они ЗАГЛУШКА, и это безопасно ровно по двум причинам, обе проверяемые:
 * ни один компонент превью их не читает (превью смотрит на наличие медиа и на `has_spoiler`),
 * и наружу этот объект не уходит — «Принять картинку» шлёт пустое тело на `visual/accept`,
 * а дескриптор собирает сервер из своей же строки актива.
 *
 * `has_spoiler: false` — не догадка: оба поля прибиты к `false` на КАЖДОМ пути записи
 * (`domain/visual.ts`, «под rich они мертвы на проводе»), то есть принятая картинка получит
 * ровно эти значения.
 */
export function proposedMediaOf(view: VisualView): DraftContent['media'] {
  if (view.state !== 'proposed') return null;
  return {
    mime_type: 'image/png',
    sha256: '',
    bytes: 0,
    show_caption_above_media: false,
    has_spoiler: false,
  };
}

/**
 * Адрес байтов кандидата. Геометрия — КОНСТАНТА контракта (§3: горизонтальный полноэкранный
 * кадр 1920×1080), а не измеренный размер файла, и берётся она отсюда именно потому, что
 * измерять нечем: `MediaRef` у предложения не приезжает. Нужна она ровно для того, чтобы
 * зарезервировать место под картинку до прихода байтов; если однажды рендер начнёт отдавать
 * другой кадр, картинка встанет с полями, а не исказится (`object-fit: contain`).
 */
export function proposedRefOf(view: VisualView): MediaRef | null {
  if (view.state !== 'proposed') return null;
  return { media_id: view.mediaId, width: VISUAL_WIDTH, height: VISUAL_HEIGHT };
}

/**
 * Строка визуала для сводки «Что будет подписано». Живёт здесь, рядом с шапкой полосы, а не
 * в диалоге: §6.5 нормативно требует, чтобы два органа на одном экране говорили о картинке
 * ОДНИМИ словами, а два словаря на один вопрос — это гарантированное расхождение.
 *
 * `proposed` сюда не доходит: гейт закрывает утверждение. Ветка всё равно есть и говорит
 * правду — молчаливое «—» на невозможном состоянии скрыло бы поломку гейта.
 */
export function visualSummary(view: VisualView): string {
  switch (view.state) {
    case 'unset':
      return 'визуал: вердикта нет — версия создана до появления механизма';
    case 'proposed':
      return 'визуал: предложение сервиса не разобрано — утверждение закрыто гейтом';
    case 'attached':
      if (view.poster !== null) {
        return `визуал: обложка «${view.poster.hook}» на ${posterDateLabel(view.poster.date)}`;
      }
      return view.origin === 'human'
        ? 'визуал: картинку загрузил человек'
        : view.origin === 'writer'
          ? 'визуал: картинку нарисовал сервис, вы её приняли'
          : 'визуал: картинка в версии, вердикта по ней нет';
    case 'none':
      return view.poster
        ? `визуал: обложки нет — ${posterReasonText(view.reason)}`
        : `визуал: сервис решил не рисовать — ${view.reason}`;
    case 'undrawn':
      return view.poster
        ? 'визуал: обложку убрали из версии — пост уйдёт без картинки'
        : 'визуал: сервис решил рисовать, но картинки в версии нет — пост уйдёт без неё';
  }
}
